import React from 'react';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';

/**
 * ProGate — renders children if user is Pro, otherwise shows an upgrade prompt.
 *
 * Usage:
 *   <ProGate feature="calendar" message="Ready to plan your week?">
 *     <CalendarContent />
 *   </ProGate>
 *
 * Or as a check:
 *   <ProGate feature="webcams" inline />
 */
export default function ProGate({ feature, message, children, inline = false, blur = false }) {
  const { isPro } = useSubscription();
  const navigate = useNavigate();

  if (isPro) return children || null;

  const defaultMessages = {
    calendar: "Ready to plan your week? Pro unlocks AI scheduling and trip planning.",
    trips: "Ready to plan your week? Pro unlocks AI scheduling and trip planning.",
    crew: "Want to connect with your crew? Pro members can add friends and compare stats.",
    webcams: "Want live eyes on the break? Webcams are available with Swell Pro.",
    trending: "Discover what's firing beyond your area with Swell Pro.",
    streak_rescue: "Your streak is on the line. Pro members can rescue streaks with Aura.",
    share_card: "Share your stoke! Shareable cards are a Pro feature.",
    automations: "Custom automations are a Pro feature.",
    integrations: "Connect external tools and services with Swell Pro.",
    analytics: "Full progression analytics are available with Swell Pro.",
    forecast: "Want to plan ahead? Pro unlocks 7-day forecasts.",
    post: "Got something to share? Pro members can post to the feed.",
    speech: "Speech-to-text is a Pro feature. Upgrade to talk to Swell.",
    notifications_smart: "Smart weather alerts are a Pro feature.",
  };

  const msg = message || defaultMessages[feature] || "This feature is available with Swell Pro.";

  if (inline) {
    return (
      <button
        data-testid={`pro-gate-${feature}`}
        onClick={() => navigate('/billing')}
        className="flex items-center gap-1.5 text-[10px] text-amber-400/80 hover:text-amber-300 transition-colors"
      >
        <Lock className="w-3 h-3" />
        <span>Pro</span>
      </button>
    );
  }

  if (blur && children) {
    return (
      <div className="relative">
        <div className="blur-sm pointer-events-none opacity-50">{children}</div>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-bg-paper/95 backdrop-blur-xl border border-amber-500/20 rounded-2xl p-5 max-w-xs text-center mx-4"
          >
            <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-sm font-medium text-white mb-1.5">Swell Pro</p>
            <p className="text-xs text-muted-foreground mb-4">{msg}</p>
            <button
              data-testid={`pro-gate-upgrade-${feature}`}
              onClick={() => navigate('/billing')}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold py-2.5 rounded-xl hover:brightness-110 transition-all"
            >
              Go Pro <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`pro-gate-${feature}`}
      className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/15 rounded-2xl p-5 text-center"
    >
      <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
        <Lock className="w-5 h-5 text-amber-400" />
      </div>
      <p className="text-sm font-medium text-white mb-1.5">Swell Pro</p>
      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{msg}</p>
      <button
        data-testid={`pro-gate-upgrade-${feature}`}
        onClick={() => navigate('/billing')}
        className="flex items-center justify-center gap-2 mx-auto bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-5 py-2.5 rounded-xl hover:brightness-110 transition-all"
      >
        <Sparkles className="w-3.5 h-3.5" />
        Go Pro
      </button>
    </motion.div>
  );
}

/**
 * ProBadge — small "PRO" indicator for locked features
 */
export function ProBadge({ onClick }) {
  const navigate = useNavigate();
  return (
    <button
      data-testid="pro-badge"
      onClick={onClick || (() => navigate('/billing'))}
      className="inline-flex items-center gap-1 text-[9px] font-bold tracking-wider bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 px-2 py-0.5 rounded-full border border-amber-500/20 hover:border-amber-500/40 transition-colors"
    >
      <Sparkles className="w-2.5 h-2.5" />
      PRO
    </button>
  );
}
